import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { global_user } from '../global/global';

export type OnboardingStep = 'survey' | 'profile' | 'salon_setup' | 'finalize';

export interface OnboardingStatus {
  user_id: number;
  current_step: 'survey' | 'profile' | 'salon_setup' | 'salon_table_setup' | 'finalize';
  steps: {
    survey: { completed: boolean; completed_at: string | null };
    profile: {
      completed: boolean;
      completed_at: string | null;
      validation?: { forward_mail_set: boolean };
    };
    salon_table_setup: {
      completed: boolean;
      completed_at: string | null;
      validation?: { salon_count: number; table_count: number };
    };
    ai_setup: { completed: boolean; completed_at: string | null };
  };
  onboarding_completed: boolean;
  can_finalize: boolean;
}

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private statusSubject = new BehaviorSubject<OnboardingStatus | null>(null);
  status$ = this.statusSubject.asObservable();
  private readonly apiBase = environment.baseUrl;
  private readonly stepOrder: OnboardingStep[] = [
    'survey',
    'profile',
    'salon_setup',
    'finalize'
  ];
  private readonly stepRoutes: Record<OnboardingStep, string> = {
    survey: '/pages/bot-training-form',
    profile: '/pages/profile',
    salon_setup: '/pages/table_mangement',
    finalize: '/onboarding/finalize'
  };

  constructor(private http: HttpClient) {}

  getStatus(force = false): Observable<OnboardingStatus> {
    const cached = this.statusSubject.value;
    if (cached && !force) {
      return of(cached);
    }

    return this.http
      .get<{ success: boolean; onboarding: OnboardingStatus }>(
        `${this.apiBase}/api/v1/onboarding/status`,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response) => response.onboarding),
        tap((status) => this.statusSubject.next(status)),
        catchError((error) => {
          console.error('Failed to get onboarding status:', error);
          return throwError(() => error);
        })
      );
  }

  refreshStatus(): Observable<OnboardingStatus> {
    return this.getStatus(true);
  }

  finalize(): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiBase}/api/v1/onboarding/finalize`,
        {},
        { headers: this.getHeaders() }
      )
      .pipe(
        catchError((error) => {
          console.error('Onboarding finalization failed:', error);
          return throwError(() => error);
        })
      );
  }

  getCurrentStep(status: OnboardingStatus): OnboardingStep {
    if (status?.steps) {
      if (!status.steps.survey?.completed) return 'survey';
      if (!status.steps.profile?.completed) return 'profile';
      if (
        !this.skipsSalonSetup() &&
        !status.steps.salon_table_setup?.completed
      ) {
        return 'salon_setup';
      }
      return 'finalize';
    }

    return this.normalizeStep(status?.current_step);
  }

  getAllowedSteps(status: OnboardingStatus): OnboardingStep[] {
    const current = this.getCurrentStep(status);
    const stepOrder = this.getStepOrder();
    const index = stepOrder.indexOf(current);
    return index === -1 ? ['survey'] : stepOrder.slice(0, index + 1);
  }

  getAllowedRoutes(status: OnboardingStatus): string[] {
    return this.getAllowedSteps(status).map((step) => this.getRouteForStep(step));
  }

  getRouteForStep(step: OnboardingStep): string {
    if (step === 'salon_setup') {
      return this.getSetupRoute();
    }
    return this.stepRoutes[step];
  }

  getSetupRoute(): string {
    return this.isTransferCompany()
      ? '/pages/transfer_management'
      : '/pages/table_mangement';
  }

  isTransferCompany(): boolean {
    const companyType = global_user?.user?.company_type || '';
    return companyType.trim().toLowerCase() === 'transfer';
  }

  isDefaultPlusCompany(): boolean {
    const companyType = global_user?.user?.company_type || '';
    return (
      companyType.trim().toLowerCase().replace(/[\s_-]+/g, '') === 'defaultplus'
    );
  }

  // Transfer + defaultPlus tenants have no salons/tables — routing them to
  // table management dead-ends onboarding (backend mirrors this in
  // isSalonTableExemptBusiness). Transfer keeps its own setup page, so only
  // defaultPlus drops the step entirely.
  private skipsSalonSetup(): boolean {
    return this.isDefaultPlusCompany();
  }

  isRouteAllowed(url: string, status: OnboardingStatus): boolean {
    const normalized = this.normalizeUrl(url);
    const allowed = this.getAllowedRoutes(status).map((route) => this.normalizeUrl(route));
    return allowed.some((route) => normalized.startsWith(route));
  }

  normalizeUrl(url: string): string {
    const stripped = url.split('?')[0].split('#')[0];
    if (stripped.length > 1) {
      return stripped.replace(/\/+$/, '');
    }
    return stripped;
  }

  private normalizeStep(step?: string | null): OnboardingStep {
    switch (step) {
      case 'profile':
        return 'profile';
      case 'salon_table_setup':
      case 'salon_setup':
        return 'salon_setup';
      case 'finalize':
        return 'finalize';
      case 'survey':
      default:
        return 'survey';
    }
  }

  private getStepOrder(): OnboardingStep[] {
    if (this.skipsSalonSetup()) {
      return this.stepOrder.filter((step) => step !== 'salon_setup');
    }
    return this.stepOrder;
  }

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : ''
    });
  }
}
